import { Component, effect, input, signal } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { CoachChat, CoachService } from '../../../../services/coach.service';
import { ZorroModule } from '../../../../modules/zorro.module';
import { ContentText } from '@mira-avant/mtlibs';
import { UserService } from '../../../../services/user.service';
import { AudioButtonComponent } from '../../audio-button/audio-button.component';

@Component({
	selector: 'app-coach-chat',
	standalone: true,
	imports: [AngularModule, ZorroModule, AudioButtonComponent],
	templateUrl: './coach-chat.component.html',
	styleUrl: './coach-chat.component.scss',
})
export class CoachChatComponent {
	takeNumber = input<number | undefined>();
	contentText = input<ContentText | undefined>();
	studentUuid = input<string | undefined>();

	loadingCoachChat = signal(false);
	chat = signal<CoachChat | undefined>(undefined);

	private npcColorMap = new Map<string, number>();

	public npcColorSchemes = [
		{ bg: '#e3f2fd', border: '#90caf9', text: '#1565c0' },
		{ bg: '#e8f5e9', border: '#a5d6a7', text: '#2e7d32' },
		{ bg: '#fff3e0', border: '#ffcc80', text: '#ef6c00' },
		{ bg: '#f3e5f5', border: '#ce93d8', text: '#7b1fa2' },
		{ bg: '#e0f7fa', border: '#80deea', text: '#00838f' },
		{ bg: '#ede7f6', border: '#b39ddb', text: '#512da8' },
		{ bg: '#ffebee', border: '#ef9a9a', text: '#c62828' },
		{ bg: '#fce4ec', border: '#f48fb1', text: '#c2185b' },
		{ bg: '#f1f8e9', border: '#c5e1a5', text: '#558b2f' },
		{ bg: '#fffde7', border: '#fff59d', text: '#f9a825' },
	];

	constructor(
		public coachService: CoachService,
		public userService: UserService
	) {
		effect(async () => {
			const take = this.takeNumber();
			const text = this.contentText();
			const uuid = this.studentUuid();

			if (take === undefined || text === undefined || uuid === undefined) {
				return;
			}

			try {
				this.loadingCoachChat.set(true);
				const cognitoUuid = await this.userService.convertUuidToCognitoUuid$(uuid);
				const chatData = await this.coachService.getCoachChat(take, text, cognitoUuid);
				this.chat.set(chatData);
			} catch (e: unknown) {
				console.error('Error getting coach chat:', e);
				this.chat.set(undefined);
			} finally {
				this.loadingCoachChat.set(false);
			}
		});
	}

	public getNpcColorIndex(name: string): number {
		if (!name || name === 'Student') return -1;

		if (this.npcColorMap.has(name)) {
			return this.npcColorMap.get(name)!;
		}

		const nextIndex = this.npcColorMap.size % this.npcColorSchemes.length;
		this.npcColorMap.set(name, nextIndex);
		return nextIndex;
	}

	public getNpcColorStyle(name?: string): object {
		const colorIndex = this.getNpcColorIndex(name || 'Assistant');
		if (colorIndex === -1) return {};

		const colorScheme = this.npcColorSchemes[colorIndex];
		return {
			'--npc-bg-color': colorScheme.bg,
			'--npc-border-color': colorScheme.border,
			'--npc-text-color': colorScheme.text,
		};
	}
}
